import type { ComponentProps, ComponentType, SVGProps } from "react";
import { ChevronDownIcon } from "lucide-react";

import { RESPONSIVE_CONTROL_HIT_AREA_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";

/**
 * The composer footer's controls — model, effort, runtime mode, plan toggle —
 * are one row of peers and have to read as one. Each of them used to carry its
 * own padding, its own muted-foreground shade, and its own chevron size, so the
 * row measured three different heights and three different icon sizes on the
 * same line. This is t3code's `ComposerControl` primitive, which is how those
 * stay identical there: one class string, applied once.
 *
 * Adapted in exactly one place. t3code pins `h-7 min-h-7` at every width; Forge
 * ships a 48px touch contract that t3code does not, so the shared geometry
 * starts at the touch minimum and tightens to t3code's density from `md`. The
 * hit area comes from `RESPONSIVE_CONTROL_HIT_AREA_CLASS`, which is also what
 * holds the row at 48px through the 640 to 767 band where `SelectTrigger`'s own
 * `sm:min-h-7` would otherwise win.
 * The `[&_svg[data-composer-control-*]]` hooks let the shared class cancel
 * `Button`'s default icon margins without every call site restating them.
 */
export const COMPOSER_CONTROL_CLASS = `${RESPONSIVE_CONTROL_HIT_AREA_CLASS} gap-1.5 whitespace-nowrap px-3 text-secondary-label transition-none hover:text-foreground md:h-7 md:min-h-0 md:min-w-0 md:px-2.5 [&_svg[data-composer-control-chevron]]:-mx-0.5 [&_svg[data-composer-control-icon]]:mx-0`;

export function ComposerControl({
  className,
  size = "sm",
  variant = "ghost",
  ...props
}: ComponentProps<typeof Button>) {
  return (
    <Button
      className={cn(COMPOSER_CONTROL_CLASS, className)}
      size={size}
      variant={variant}
      {...props}
    />
  );
}

/**
 * The hairline between two controls in the footer row.
 *
 * It lives here rather than in each control's file because a control that can
 * hide itself has to hide its own leading separator with it. The reasoning
 * effort picker is the case that forced this: it returns nothing when the
 * selected model has no options, and a separator left behind by its caller
 * would print a stray hairline with nothing on either side of it.
 */
export function ComposerControlSeparator() {
  return <Separator orientation="vertical" className="mx-0.5 hidden h-4 sm:block" />;
}

/**
 * A control's leading glyph. Fixed at the row's icon size so a control that
 * happens to have an icon still lines up with one that does not — the effort
 * control has no leading icon in t3code either, and that only looks
 * intentional when every other glyph on the row is the same size.
 */
export function ComposerControlIcon({
  icon: Icon,
  className,
  opticalSize = "default",
}: {
  // Not `LucideIcon`: the provider glyphs are hand-authored SVG components,
  // and they belong on this row too.
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
  readonly className?: string | undefined;
  readonly opticalSize?: "default" | "large";
}) {
  return (
    <Icon
      aria-hidden="true"
      className={cn("shrink-0", opticalSize === "large" ? "size-4.5" : "size-4", className)}
      data-composer-control-icon
    />
  );
}

/**
 * The trailing "this opens a menu" glyph. Smaller and quieter than a leading
 * icon on purpose: it is chrome, not content, and it repeats on every control
 * in the row.
 */
export function ComposerControlChevron() {
  return (
    <ChevronDownIcon
      aria-hidden="true"
      className="-mx-0.5 size-3.5 shrink-0 text-icon-muted"
      data-composer-control-chevron
      strokeWidth={2.25}
    />
  );
}
